import React from 'react';
import {
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
} from '@mui/material';
import { useCurriculums, Curriculum } from './hooks';

interface CurriculumSelectorProps {
  onCurriculumSelect: (curriculum: Curriculum | null) => void;
  selectedCurriculum: Curriculum | null;
  districtId: number | undefined;
}

const CurriculumSelector: React.FC<CurriculumSelectorProps> = ({
  onCurriculumSelect,
  selectedCurriculum,
  districtId,
}) => {
  const { data: curriculums, isLoading: isLoadingCurriculums } = useCurriculums(districtId);

  // Auto-select first curriculum when curriculums load
  React.useEffect(() => {
    if (curriculums && curriculums.length > 0 && !selectedCurriculum) {
      onCurriculumSelect(curriculums[0]);
    }
  }, [curriculums, selectedCurriculum, onCurriculumSelect]);

  const handleCurriculumChange = (event: SelectChangeEvent<number>) => {
    const curriculumId = event.target.value as number;
    const curriculum = curriculums?.find((c) => c.id === curriculumId);
    if (curriculum) {
      onCurriculumSelect(curriculum);
    } else {
      onCurriculumSelect(null);
    }
  };

  if (!districtId) {
    return (
      <Box sx={{ p: 2, mb: 3, bgcolor: 'warning.light', borderRadius: 1 }}>
        No district data available. Please ensure you have district admin access.
      </Box>
    );
  }

  return (
    <Box sx={{ mb: 3 }}>
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        <FormControl sx={{ minWidth: 200 }} size="small">
          <InputLabel id="curriculum-select-label">Curriculum</InputLabel>
          <Select
            labelId="curriculum-select-label"
            id="curriculum-select"
            value={selectedCurriculum?.id || ''}
            label="Curriculum"
            onChange={handleCurriculumChange}
            disabled={isLoadingCurriculums || !curriculums?.length}
          >
            {isLoadingCurriculums ? (
              <MenuItem value="">Loading...</MenuItem>
            ) : (
              curriculums?.map((curriculum) => (
                <MenuItem key={curriculum.id} value={curriculum.id}>
                  {curriculum.name}
                </MenuItem>
              ))
            )}
          </Select>
        </FormControl>
      </Box>
    </Box>
  );
};

export default CurriculumSelector;
